import { useQuery } from "@tanstack/react-query";
import { Button, Input } from "@theg1group/ui";
import cytoscape, { type Core } from "cytoscape";
import fcose from "cytoscape-fcose";
import { useEffect, useMemo, useRef, useState } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.ts";
import { fmtDate } from "../lib/format.ts";
import type { GraphData, GraphLabels, GraphLayer, GraphNode } from "../lib/types.ts";

cytoscape.use(fcose);

export const LAYER_COLOR: Record<GraphLayer, string> = {
	partner: "#2563eb",
	counterparty: "#64748b",
	service: "#d97706",
	sanctioned: "#dc2626",
	supernode: "#9333ea",
};

/** Node-type toggles (allow-list). "cluster" mirrors the API param even though no node
 *  currently carries that layer — kept for parity with /api/graph nodeTypes. */
const NODE_TYPE_OPTS = [
	"partner",
	"counterparty",
	"service",
	"sanctioned",
	"supernode",
	"cluster",
] as const;

/** Label categories the operator can HIDE ("custodial / cold / exchange emitters"). */
const LABEL_HIDE_OPTS: Array<{ cat: string; label: string }> = [
	{ cat: "exchange", label: "exchange" },
	{ cat: "exchange_hot", label: "exchange · hot" },
	{ cat: "exchange_deposit", label: "exchange · deposit" },
	{ cat: "bridge", label: "bridge" },
	{ cat: "mixer", label: "mixer" },
	{ cat: "service", label: "service / cold" },
];

const DUST_USD = 1;

export interface GraphViewProps {
	/** `partner:<id>` | `address:<id>` | `cluster:<id>`; blank = whole graph (capped server-side). */
	focus?: string;
	depth: number;
	minUsd: number;
	/** Called when the user picks "Focus here" in the inspector (page decides how to apply it). */
	onFocus?: (focus: string) => void;
	height?: string;
}

interface SelectedEdge {
	id: string;
	rel: string;
	weight?: number;
	meta?: { txCount?: number; firstTs?: string; lastTs?: string };
	sourceLabel: string;
	targetLabel: string;
}

type Selected = { kind: "node"; node: GraphNode } | { kind: "edge"; edge: SelectedEdge };

export function GraphView({ focus, depth, minUsd, onFocus, height }: GraphViewProps) {
	const navigate = useNavigate();
	const cyRef = useRef<Core | null>(null);
	const [enabledRels, setEnabledRels] = useState<Set<string> | null>(null);
	const [selected, setSelected] = useState<Selected | null>(null);

	// ---- filter arsenal (client-side for instant toggling + server params to bound payload) ----
	const [nodeTypes, setNodeTypes] = useState<Set<string>>(() => new Set(NODE_TYPE_OPTS));
	const [hideLabels, setHideLabels] = useState<Set<string>>(() => new Set());
	const [hideDust, setHideDust] = useState(false);
	const [minUsdF, setMinUsdF] = useState(minUsd);
	const [maxUsdF, setMaxUsdF] = useState(0);
	// The page toolbar can drive minUsd via props — keep the rail input in sync.
	useEffect(() => setMinUsdF(minUsd), [minUsd]);

	const labels = useQuery({
		queryKey: ["graph-labels"],
		queryFn: () => api.get<GraphLabels>("/api/graph/labels"),
	});

	const typesFull = nodeTypes.size === 0 || nodeTypes.size >= NODE_TYPE_OPTS.length;
	const typesCsv = typesFull ? "" : [...nodeTypes].sort().join(",");
	const labelsCsv = [...hideLabels].sort().join(",");

	const qs = new URLSearchParams();
	if (focus) qs.set("focus", focus);
	qs.set("depth", String(depth));
	if (minUsdF > 0) qs.set("minUsd", String(minUsdF));
	if (maxUsdF > 0) qs.set("maxUsd", String(maxUsdF));
	if (hideDust) qs.set("hideDust", "1");
	if (typesCsv) qs.set("nodeTypes", typesCsv);
	if (labelsCsv) qs.set("hideLabels", labelsCsv);
	const graph = useQuery({
		queryKey: ["graph", focus ?? "", depth, minUsdF, maxUsdF, hideDust, typesCsv, labelsCsv],
		queryFn: () => api.get<GraphData>(`/api/graph?${qs.toString()}`),
	});

	const rels = useMemo(
		() => enabledRels ?? new Set((labels.data?.rels ?? []).map((r) => r.rel)),
		[enabledRels, labels.data],
	);

	const nodeById = useMemo(() => {
		const m = new Map<string, GraphNode>();
		for (const n of graph.data?.nodes ?? []) m.set(n.id, n);
		return m;
	}, [graph.data]);

	const elements = useMemo(() => {
		if (!graph.data) return [];
		// Node visibility: partner nodes, the focus node and partner-owned addresses are never hidden.
		const keepNode = (n: GraphNode) => {
			if (n.type === "partner" || n.id === focus) return true;
			if ((n.meta.partnerIds?.length ?? 0) > 0) return true;
			if (!typesFull && !nodeTypes.has(n.layer)) return false;
			if (hideLabels.size && (n.meta.labels ?? []).some((l) => hideLabels.has(l))) return false;
			return true;
		};
		const visibleNodes = graph.data.nodes.filter(keepNode);
		const visibleIds = new Set(visibleNodes.map((n) => n.id));
		const edges = graph.data.edges.filter((e) => {
			if (!rels.has(e.rel)) return false;
			if (!visibleIds.has(e.source) || !visibleIds.has(e.target)) return false;
			if (e.rel === "FLOW") {
				const w = e.weight ?? 0;
				if (minUsdF > 0 && w < minUsdF) return false;
				if (maxUsdF > 0 && w > maxUsdF) return false;
				if (hideDust && w < DUST_USD) return false;
			}
			return true;
		});
		const keep = new Set<string>();
		for (const e of edges) {
			keep.add(e.source);
			keep.add(e.target);
		}
		const nodeEls = visibleNodes
			.filter((n) => keep.has(n.id) || !edges.length || n.id === focus)
			.map((n) => ({
				data: {
					id: n.id,
					label: n.label,
					type: n.type,
					layer: n.layer,
					isFocus: n.id === focus ? 1 : 0,
					risk: (n.meta.riskScore ?? 0) >= 60 ? 1 : 0,
				},
			}));
		const edgeEls = edges.map((e) => ({
			data: {
				id: e.id,
				source: e.source,
				target: e.target,
				label: e.rel,
				rel: e.rel,
				weight: e.weight ?? 0,
				txCount: e.meta?.txCount,
				firstTs: e.meta?.firstTs,
				lastTs: e.meta?.lastTs,
			},
		}));
		return [...nodeEls, ...edgeEls];
	}, [graph.data, rels, focus, typesFull, nodeTypes, hideLabels, hideDust, minUsdF, maxUsdF]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed on element count
	const layoutOpts = useMemo(
		() => ({
			name: "fcose",
			animate: false,
			randomize: true,
			fit: true,
			padding: 40,
			stop: () => {
				const cy = cyRef.current;
				if (!cy) return;
				try {
					if (cy.zoom() > 1.1) {
						cy.zoom(1.1);
						cy.center();
					}
				} catch {
					// ignore
				}
			},
		}),
		[elements.length],
	);

	const stylesheet = useMemo(
		() => [
			{
				selector: "node",
				style: {
					"background-color": (n: cytoscape.NodeSingular) =>
						LAYER_COLOR[n.data("layer") as GraphLayer] ?? "#64748b",
					label: "data(label)",
					"font-size": "8px",
					color: "#111827",
					"text-wrap": "ellipsis",
					"text-max-width": "90px",
					"text-valign": "bottom",
					"text-margin-y": 3,
					width: 16,
					height: 16,
				},
			},
			{
				selector: "node[type = 'partner']",
				style: {
					shape: "diamond",
					width: 28,
					height: 28,
					"font-size": "9px",
					"font-weight": "bold",
				},
			},
			{
				selector: "node[layer = 'supernode']",
				style: { width: 22, height: 22 },
			},
			// High-risk ring (red) — overridden by the focus ring below when a node is both.
			{
				selector: "node[risk = 1]",
				style: { "border-width": 3, "border-color": "#dc2626" },
			},
			{
				selector: "node[isFocus = 1]",
				style: { "border-width": 3, "border-color": "#111827" },
			},
			{
				selector: "edge",
				style: {
					width: (e: cytoscape.EdgeSingular) => 1 + Math.log10((e.data("weight") ?? 0) + 1),
					"line-color": "#cbd5e1",
					"target-arrow-color": "#cbd5e1",
					"target-arrow-shape": "triangle",
					"arrow-scale": 0.6,
					"curve-style": "bezier",
					label: "data(label)",
					"font-size": "6px",
					color: "#94a3b8",
				},
			},
			{
				selector: "edge[rel = 'OWNS']",
				style: { "line-style": "dashed", "line-color": "#93c5fd", "target-arrow-color": "#93c5fd" },
			},
			{
				selector: "edge[rel = 'SAME_CLUSTER']",
				style: { "line-style": "dashed", "line-color": "#c084fc", "target-arrow-color": "#c084fc" },
			},
			{
				selector: "edge[rel = 'FUNDED_BY']",
				style: { "line-color": "#fcd34d", "target-arrow-color": "#fcd34d" },
			},
			{
				selector: "edge[rel = 'DEPOSITS_TO']",
				style: { "line-color": "#fca5a5", "target-arrow-color": "#fca5a5" },
			},
			{ selector: "node:selected", style: { "border-width": 3, "border-color": "#111827" } },
			{
				selector: "edge:selected",
				style: { "line-color": "#111827", "target-arrow-color": "#111827" },
			},
		],
		[],
	);

	const h = height ?? "calc(100vh - 220px)";

	const toggle = (set: Set<string>, key: string, on: boolean) => {
		const next = new Set(set);
		if (on) next.add(key);
		else next.delete(key);
		return next;
	};

	return (
		<div className="flex gap-3" style={{ height: h, minHeight: 420 }}>
			{/* Filter rail + legend */}
			<div className="w-52 shrink-0 overflow-auto rounded-md border border-line bg-surface p-3">
				<div className="text-[11px] uppercase text-muted mb-2">Relationships</div>
				{(labels.data?.rels ?? []).map((r) => (
					<label
						key={r.rel}
						className="flex items-center gap-2 text-xs text-content mb-1 cursor-pointer"
					>
						<input
							type="checkbox"
							checked={rels.has(r.rel)}
							onChange={(e) => setEnabledRels(toggle(rels, r.rel, e.target.checked))}
						/>
						{r.rel} <span className="text-muted">({r.count})</span>
					</label>
				))}
				{!labels.data?.rels.length && <div className="text-xs text-muted">—</div>}

				<div className="text-[11px] uppercase text-muted mt-4 mb-2">Node types</div>
				{NODE_TYPE_OPTS.map((t) => (
					<label
						key={t}
						className="flex items-center gap-2 text-xs text-content mb-1 cursor-pointer"
					>
						<input
							type="checkbox"
							checked={nodeTypes.has(t)}
							onChange={(e) => setNodeTypes(toggle(nodeTypes, t, e.target.checked))}
						/>
						{t in LAYER_COLOR && (
							<span
								className="inline-block h-2.5 w-2.5 rounded-pill"
								style={{ background: LAYER_COLOR[t as GraphLayer] }}
							/>
						)}
						{t}
					</label>
				))}

				<div className="text-[11px] uppercase text-muted mt-4 mb-2">Hide labels</div>
				{LABEL_HIDE_OPTS.map((o) => (
					<label
						key={o.cat}
						className="flex items-center gap-2 text-xs text-content mb-1 cursor-pointer"
					>
						<input
							type="checkbox"
							checked={hideLabels.has(o.cat)}
							onChange={(e) => setHideLabels(toggle(hideLabels, o.cat, e.target.checked))}
						/>
						{o.label}
					</label>
				))}

				<div className="text-[11px] uppercase text-muted mt-4 mb-2">Flow (USD)</div>
				<label className="flex items-center gap-2 text-xs text-content mb-2 cursor-pointer">
					<input
						type="checkbox"
						checked={hideDust}
						onChange={(e) => setHideDust(e.target.checked)}
					/>
					Hide dust (&lt; ${DUST_USD})
				</label>
				<div className="flex gap-1.5">
					<Input
						type="number"
						min={0}
						value={minUsdF ? String(minUsdF) : ""}
						onChange={(e) => setMinUsdF(Math.max(0, Number(e.target.value) || 0))}
						placeholder="min"
						size="sm"
						aria-label="Min USD"
					/>
					<Input
						type="number"
						min={0}
						value={maxUsdF ? String(maxUsdF) : ""}
						onChange={(e) => setMaxUsdF(Math.max(0, Number(e.target.value) || 0))}
						placeholder="max"
						size="sm"
						aria-label="Max USD"
					/>
				</div>

				{graph.data && (
					<div className="text-[11px] text-muted mt-4">
						{graph.data.nodes.length} nodes · {graph.data.edges.length} edges
					</div>
				)}
			</div>

			{/* Canvas */}
			<div className="flex-1 rounded-md border border-line bg-surface overflow-hidden">
				{graph.isLoading ? (
					<div className="p-4 text-muted">Loading…</div>
				) : graph.isError ? (
					<div className="p-4 text-danger text-sm">{String(graph.error)}</div>
				) : !elements.length ? (
					<div className="p-4 text-muted text-sm">No graph data for this focus.</div>
				) : (
					<CytoscapeComponent
						elements={elements}
						layout={layoutOpts}
						stylesheet={stylesheet as never}
						style={{ width: "100%", height: "100%" }}
						cy={(cy: Core) => {
							cyRef.current = cy;
							cy.maxZoom(1.5);
							cy.minZoom(0.15);
							cy.removeListener("tap");
							cy.on("tap", "node", (evt) => {
								const node = nodeById.get(evt.target.id());
								if (node) setSelected({ kind: "node", node });
							});
							cy.on("tap", "edge", (evt) => {
								const d = evt.target.data();
								setSelected({
									kind: "edge",
									edge: {
										id: d.id,
										rel: d.rel,
										weight: d.weight,
										meta: { txCount: d.txCount, firstTs: d.firstTs, lastTs: d.lastTs },
										sourceLabel: nodeById.get(d.source)?.label ?? d.source,
										targetLabel: nodeById.get(d.target)?.label ?? d.target,
									},
								});
							});
							cy.on("tap", (evt) => {
								if (evt.target === cy) setSelected(null);
							});
						}}
					/>
				)}
			</div>

			{/* Inspector */}
			{selected && (
				<div className="w-64 shrink-0 overflow-auto rounded-md border border-line bg-surface p-3">
					{selected.kind === "node" ? (
						<NodeInspector
							node={selected.node}
							onOpen={() => {
								const [type, id] = selected.node.id.split(":");
								navigate(type === "partner" ? `/partners/${id}` : `/addresses/${id}`);
							}}
							onFocus={onFocus ? () => onFocus(selected.node.id) : undefined}
						/>
					) : (
						<EdgeInspector edge={selected.edge} />
					)}
				</div>
			)}
		</div>
	);
}

function NodeInspector({
	node,
	onOpen,
	onFocus,
}: {
	node: GraphNode;
	onOpen: () => void;
	onFocus?: () => void;
}) {
	const m = node.meta ?? {};
	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-2">
				<span
					className="inline-block h-2.5 w-2.5 rounded-pill"
					style={{ background: LAYER_COLOR[node.layer] ?? "#64748b" }}
				/>
				<span className="text-[11px] uppercase text-muted">
					{node.type} · {node.layer}
				</span>
			</div>
			<div className="text-sm text-content font-medium break-all font-mono">{node.label}</div>
			<dl className="text-xs grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
				{m.chain && <KV k="chain" v={m.chain} />}
				{m.kind && <KV k="kind" v={m.kind} />}
				{m.status && <KV k="status" v={m.status} />}
				{m.riskScore !== undefined && <KV k="risk" v={String(m.riskScore)} />}
				{m.partnerNames?.length ? <KV k="partners" v={m.partnerNames.join(", ")} /> : null}
				{m.labels?.length ? <KV k="labels" v={m.labels.join(", ")} /> : null}
			</dl>
			<div className="flex flex-col gap-1.5 mt-1">
				<Button size="sm" variant="outline" onClick={onOpen}>
					{node.type === "partner" ? "Open partner →" : "Open address →"}
				</Button>
				{onFocus && (
					<Button size="sm" variant="ghost" onClick={onFocus}>
						Focus here
					</Button>
				)}
			</div>
		</div>
	);
}

function EdgeInspector({ edge }: { edge: SelectedEdge }) {
	return (
		<div className="flex flex-col gap-2">
			<div className="text-[11px] uppercase text-muted">edge · {edge.rel}</div>
			<div className="text-xs text-content font-mono break-all">
				{edge.sourceLabel} → {edge.targetLabel}
			</div>
			<dl className="text-xs grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
				{edge.weight !== undefined && <KV k="weight" v={String(edge.weight)} />}
				{edge.meta?.txCount !== undefined && <KV k="tx" v={String(edge.meta.txCount)} />}
				{edge.meta?.firstTs && <KV k="first" v={fmtDate(edge.meta.firstTs)} />}
				{edge.meta?.lastTs && <KV k="last" v={fmtDate(edge.meta.lastTs)} />}
			</dl>
		</div>
	);
}

function KV({ k, v }: { k: string; v: string }) {
	return (
		<>
			<dt className="text-muted">{k}</dt>
			<dd className="text-content break-all">{v}</dd>
		</>
	);
}
